/*****************************************************
 * VasteZendingenBeheer.jsx
 * Locatie: src/components/VasteZendingenBeheer.jsx
 * Versie: 1.0.0
 ******************************************************
 * Overzicht van alle vaste (periodieke) zendingen.
 * Beschikbaar in admin dashboard en klantenportaal.
 * Functies: bekijken, bewerken, pauzeren, verwijderen.
 *****************************************************/

import React, { useState, useEffect, useCallback } from 'react';
import VasteZendingModal from './VasteZendingModal';

const G = {
  navy:'#1a364c', gold:'#d4a848', bg:'#f8fafc',
  border:'#e2e8f0', txt:'#1e293b', txt2:'#64748b', txt3:'#94a3b8',
  green:'#16a34a', greenL:'#dcfce7', orange:'#f59e0b', orangeL:'#fef3c7',
  red:'#dc2626', redL:'#fee2e2',
};

const FREQ_LABEL = {
  dagelijks  : '📅 Dagelijks',
  wekelijks  : '📅 Wekelijks',
  maandelijks: '📅 Maandelijks',
  vaste_dagen: '📅 Vaste data',
};

function schemaOmschrijving(z) {
  const DAGEN = { maandag:'Ma', dinsdag:'Di', woensdag:'Wo', donderdag:'Do', vrijdag:'Vr', zaterdag:'Za', zondag:'Zo' };
  switch (z.frequentie) {
    case 'dagelijks'  : return 'Elke dag';
    case 'maandelijks': return `De ${z.maandelijkse_datum}e van de maand`;
    case 'wekelijks'  : {
      const dagen = typeof z.wekelijkse_dagen === 'string'
        ? JSON.parse(z.wekelijkse_dagen || '[]')
        : z.wekelijkse_dagen || [];
      return `Elke ${dagen.map(d => DAGEN[d] || d).join(', ')}`;
    }
    case 'vaste_dagen': {
      const dagen = typeof z.vaste_dagen === 'string'
        ? JSON.parse(z.vaste_dagen || '[]')
        : z.vaste_dagen || [];
      return `${dagen.length} vaste datum${dagen.length !== 1 ? 's' : ''}`;
    }
    default: return z.frequentie;
  }
}

export default function VasteZendingenBeheer({ klantId = null, isKlantPortaal = false }) {
  const [zendingen, setZendingen]     = useState([]);
  const [laden, setLaden]             = useState(true);
  const [editModal, setEditModal]     = useState(null); // null of zending-object
  const [nieuwModal, setNieuwModal]   = useState(false);
  const [bevestigDel, setBevestigDel] = useState(null);

  const laadZendingen = useCallback(async () => {
    setLaden(true);
    try {
      const url = klantId
        ? `/api/vaste-zendingen/klant/${klantId}`
        : '/api/vaste-zendingen';
      const resp = await fetch(url, { credentials: 'include' });
      const data = await resp.json();
      if (data.success) setZendingen(data.data || []);
    } catch(e) {
      console.error('VasteZendingenBeheer:', e);
    }
    setLaden(false);
  }, [klantId]);

  useEffect(() => { laadZendingen(); }, [laadZendingen]);

  const toggleActief = async (id) => {
    await fetch(`/api/vaste-zendingen/${id}/pause`, {
      method: 'POST', credentials: 'include',
    });
    laadZendingen();
  };

  const verwijder = async (id) => {
    await fetch(`/api/vaste-zendingen/${id}`, {
      method: 'DELETE', credentials: 'include',
    });
    setBevestigDel(null);
    laadZendingen();
  };

  if (laden) return (
    <div style={{ textAlign:'center', padding:32, color:G.txt3 }}>Laden...</div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:G.txt }}>
            📦 Vaste zendingen
          </h3>
          <p style={{ margin:'4px 0 0', fontSize:12, color:G.txt3 }}>
            Periodieke aanvragen worden automatisch aangemaakt
          </p>
        </div>
        <button
          onClick={() => setNieuwModal(true)}
          style={{ background:G.navy, color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
          + Nieuwe vaste zending
        </button>
      </div>

      {zendingen.length === 0 ? (
        <div style={{ textAlign:'center', padding:40, color:G.txt3, background:G.bg, borderRadius:10, border:`1px dashed ${G.border}` }}>
          <div style={{ fontSize:32, marginBottom:8 }}>📦</div>
          <div style={{ fontSize:14 }}>Nog geen vaste zendingen ingesteld</div>
          <div style={{ fontSize:12, marginTop:4 }}>Klik op "+ Nieuwe vaste zending" om te beginnen</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {zendingen.map(z => (
            <div key={z.id} style={{
              background:'#fff', border:`1px solid ${G.border}`, borderRadius:10,
              padding:'14px 18px', display:'flex', alignItems:'center', gap:12,
              opacity: z.actief ? 1 : 0.6,
            }}>
              {/* Status indicator */}
              <div style={{
                width:10, height:10, borderRadius:'50%', flexShrink:0,
                background: z.actief ? G.green : G.txt3,
              }} />

              {/* Info */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontWeight:700, fontSize:14, color:G.txt }}>
                    {z.naam || `${z.laad_plaats} → ${z.los_plaats}`}
                  </span>
                  <span style={{ fontSize:11, background: z.actief ? G.greenL : G.bg,
                    color: z.actief ? G.green : G.txt3,
                    padding:'2px 8px', borderRadius:10, fontWeight:600 }}>
                    {z.actief ? 'Actief' : 'Gepauzeerd'}
                  </span>
                </div>
                <div style={{ fontSize:12, color:G.txt2 }}>
                  {z.laad_plaats} → {z.los_plaats}
                  <span style={{ margin:'0 8px', color:G.txt3 }}>·</span>
                  {schemaOmschrijving(z)}
                  <span style={{ margin:'0 8px', color:G.txt3 }}>·</span>
                  {z.dagen_voor_laad}d van tevoren
                </div>
                {z.volgende_aanmaak && z.actief && (
                  <div style={{ fontSize:11, color:G.gold, marginTop:4 }}>
                    ⏰ Volgende aanvraag: {new Date(z.volgende_aanmaak).toLocaleDateString('nl-NL', { day:'2-digit', month:'long', year:'numeric' })}
                  </div>
                )}
                {z.laatste_aanvraag_id && (
                  <div style={{ fontSize:11, color:G.txt3, marginTop:2 }}>
                    Laatste aanvraag: {z.laatste_aanvraag_id}
                  </div>
                )}
              </div>

              {/* Acties */}
              <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                <button onClick={() => setEditModal(z)}
                  style={{ padding:'6px 12px', border:`1px solid ${G.border}`, borderRadius:7, background:'#fff', color:G.txt2, fontSize:12, cursor:'pointer' }}>
                  ✏️ Bewerken
                </button>
                <button onClick={() => toggleActief(z.id)}
                  style={{ padding:'6px 12px', border:`1px solid ${G.border}`, borderRadius:7,
                    background: z.actief ? G.orangeL : G.greenL,
                    color: z.actief ? G.orange : G.green,
                    fontSize:12, cursor:'pointer', fontWeight:600 }}>
                  {z.actief ? '⏸ Pauzeer' : '▶ Hervat'}
                </button>
                <button onClick={() => setBevestigDel(z.id)}
                  style={{ padding:'6px 10px', border:`1px solid ${G.red}20`, borderRadius:7, background:G.redL, color:G.red, fontSize:12, cursor:'pointer' }}>
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Verwijder bevestiging */}
      {bevestigDel && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, maxWidth:380, textAlign:'center' }}>
            <div style={{ fontSize:32, marginBottom:12 }}>🗑️</div>
            <h3 style={{ margin:'0 0 8px', color:G.txt }}>Vaste zending verwijderen?</h3>
            <p style={{ color:G.txt2, fontSize:13, margin:'0 0 20px' }}>
              Dit kan niet ongedaan worden gemaakt. Eerder aangemaakte aanvragen blijven bestaan.
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button onClick={() => setBevestigDel(null)}
                style={{ padding:'9px 20px', border:`1px solid ${G.border}`, borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>
                Annuleren
              </button>
              <button onClick={() => verwijder(bevestigDel)}
                style={{ padding:'9px 20px', border:'none', borderRadius:8, background:G.red, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600 }}>
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <VasteZendingModal
        show={!!editModal}
        onClose={() => setEditModal(null)}
        onSave={() => { setEditModal(null); laadZendingen(); }}
        initialData={editModal}
        klantId={klantId}
      />
      <VasteZendingModal
        show={nieuwModal}
        onClose={() => setNieuwModal(false)}
        onSave={() => { setNieuwModal(false); laadZendingen(); }}
        klantId={klantId}
      />
    </div>
  );
}
